import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, ArrowLeft, FileText, Info } from 'lucide-react';
import { stripPatientGuidance } from '@/lib/api-message';

type Arm = 'pregnancy' | 'preconception';

interface ResultRow {
  tier: number;
  tier_description: string;
  clinical_dose_primary: number;
  clinical_dose_secondary: number | null;
  dose_concordance_percentage: number;
  absolute_dose_difference: number;
  api_recommended_dose: number;
  api_treatment_recommendation: string;
  api_message: string | null;
  strategy_used: string | null;
}

const tableMap = {
  pregnancy: {
    concordance: 'concordance_results' as const,
    apiResponses: 'api_responses' as const,
    cases: 'clinical_cases' as const,
    labels: 'case_labels' as const,
    formPath: '/pregnancy',
  },
  preconception: {
    concordance: 'preconception_concordance_results' as const,
    apiResponses: 'preconception_api_responses' as const,
    cases: 'preconception_cases' as const,
    labels: 'preconception_case_labels' as const,
    formPath: '/preconception',
  },
} as const;

const tierColor = (tier: number) =>
  tier === 1 ? 'bg-green-100 text-green-800 border-green-300' :
  tier === 2 ? 'bg-[#BEC5A4] text-[#33401c] border-[#9fa87e]' :
  tier === 3 ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
  'bg-red-100 text-red-800 border-red-300';

const ResultPage = () => {
  const { arm: armParam, caseId } = useParams<{ arm: Arm; caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<ResultRow | null>(null);
  const [caseNo, setCaseNo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);

  const arm: Arm | undefined = armParam === 'pregnancy' || armParam === 'preconception' ? armParam : undefined;

  useEffect(() => {
    if (!arm || !caseId) {
      setError('Μη έγκυρη διεύθυνση.');
      setLoading(false);
      return;
    }
    const tables = tableMap[arm];

    (async () => {
      const { data: concordance, error: cErr } = await supabase
        .from(tables.concordance)
        .select('*')
        .eq('case_id', caseId)
        .maybeSingle();

      if (cErr || !concordance) {
        setError('Δεν βρέθηκε αποτέλεσμα σύγκρισης για αυτήν την υπόθεση.');
        setLoading(false);
        return;
      }

      const { data: apiResp } = await supabase
        .from(tables.apiResponses)
        .select('*')
        .eq('case_id', caseId)
        .is('api_error', null)
        .maybeSingle();

      const apiResponseData = (apiResp?.api_response_data ?? {}) as Record<string, unknown>;

      // Non-fatal: if the row is missing, log and skip the callout. Prefix per arm.
      const { data: caseRow, error: caseNoErr } = await supabase.from(tables.cases)
        .select('study_case_no').eq('id', caseId).maybeSingle();
      if (caseNoErr) console.error('[ResultPage] study_case_no fetch failed', caseNoErr);
      const n = caseRow?.study_case_no;
      if (n != null) setCaseNo(`${arm === 'pregnancy' ? 'P' : 'C'}-${n}`);

      const { data: labelRow } = await supabase
        .from(tables.labels).select('label').eq('case_id', caseId).maybeSingle();
      if (labelRow?.label) setLabel(labelRow.label);

      setData({
        tier: (concordance as any).tier ?? 0,
        tier_description: (concordance as any).tier_description ?? '',
        clinical_dose_primary: (concordance as any).clinical_dose_primary ?? 0,
        clinical_dose_secondary: (concordance as any).clinical_dose_secondary ?? null,
        dose_concordance_percentage: concordance.dose_concordance_percentage ?? 0,
        absolute_dose_difference: concordance.absolute_dose_difference ?? 0,
        api_recommended_dose: apiResp?.api_recommended_dose ?? 0,
        api_treatment_recommendation: apiResp?.api_treatment_recommendation ?? '',
        // Display copy only — the stored api_response_data.message stays verbatim.
        api_message: stripPatientGuidance(apiResponseData.message as string | undefined),
        strategy_used: (apiResp as any)?.strategy_used ?? (apiResponseData.strategy_used as string) ?? null,
      });
      setLoading(false);
    })();
  }, [arm, caseId]);

  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('id').eq('user_id', user.id).single()
      .then(({ data: p }) => { if (p) setProfileId(p.id); });
  }, [user]);

  const saveLabel = async () => {
    if (!arm || !caseId || !profileId) return;
    setSavingLabel(true);
    const { error: e } = await supabase.from(tableMap[arm].labels).upsert({
      case_id: caseId, clinician_id: profileId, label: label.trim(),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'case_id' });
    setSavingLabel(false);
    if (e) toast({ variant: 'destructive', title: 'Αποτυχία αποθήκευσης', description: e.message });
    else toast({ title: 'Το ιδιωτικό σημείωμα αποθηκεύτηκε' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8f7f2' }}>
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error || !data || !arm) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#f8f7f2' }}>
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Δεν βρέθηκε αποτέλεσμα</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              {error ?? 'Η σύγκριση δεν είναι διαθέσιμη.'}
            </p>
            <Button onClick={() => navigate('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Επιστροφή
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tables = tableMap[arm];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8f7f2' }}>
      <header className="border-b" style={{ backgroundColor: arm === 'pregnancy' ? '#F3E5E0' : '#D4D8C7' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-800">
            Αποτέλεσμα Σύγκρισης
          </h1>
          <Button variant="outline" onClick={() => navigate(tables.formPath)}>
            <FileText className="w-4 h-4 mr-2" />
            Νέα Αξιολόγηση
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card className="max-w-2xl mx-auto shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl">Tier {data.tier}</CardTitle>
              <Badge variant="outline" className={`text-base px-3 py-1 ${tierColor(data.tier)}`}>
                {data.tier_description || `Tier ${data.tier}`}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 font-mono mt-2">Case {caseId?.slice(0, 8)}…</p>
            {caseNo && (
              <div
                className="mt-3 rounded-md border px-3 py-2"
                style={{ backgroundColor: 'var(--brand-wash)', borderColor: 'var(--brand-edge)' }}
              >
                <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--brand)' }}>Αρ. Μελέτης</span>
                <div className="text-lg font-bold font-mono" style={{ color: 'var(--ink)' }}>{caseNo}</div>
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Καταγράψτε τον στον φάκελο της ασθενούς για την επανεξέταση.</span>
              </div>
            )}

            <div className="mt-3">
              <span className="text-xs uppercase tracking-wide text-gray-500">Ιδιωτικό σημείωμα (προαιρετικό)</span>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Προσθέστε αρχικά ή μνημονικό κανόνα"
                  className="h-8 text-sm"
                />
                <Button size="sm" variant="outline" onClick={saveLabel} disabled={savingLabel}>
                  {savingLabel && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Αποθήκευση
                </Button>
              </div>
              <Collapsible open={tipOpen} onOpenChange={setTipOpen}>
                <CollapsibleTrigger className="mt-1 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                  <Info className="w-3 h-3" /> Τι είναι αυτό;
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1 rounded border bg-gray-50 p-2 text-xs text-gray-600">
                  Προσθέστε <b>αρχικά ή μνημονικό κανόνα</b> για να αναγνωρίζετε την ασθενή στην επανεξέταση.
                  Ορατό μόνο σε εσάς και τον Υπεύθυνο Ερευνητή· <b>δεν</b> αποτελεί μέρος των ερευνητικών
                  δεδομένων και δεν εξάγεται στην ανάλυση. Μην χρησιμοποιείτε πλήρες ονοματεπώνυμο.
                </CollapsibleContent>
              </Collapsible>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-xs uppercase text-gray-500 tracking-wide">Δόση Ειδικού</div>
                <div className="text-2xl font-semibold text-gray-800">
                  {data.clinical_dose_primary} mcg/ημ.
                </div>
                {data.clinical_dose_secondary !== null && (
                  <div className="text-xs text-gray-500">
                    Εναλλακτική: {data.clinical_dose_secondary} mcg/ημ.
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <div className="text-xs uppercase text-gray-500 tracking-wide">Δόση Αλγορίθμου</div>
                <div className="text-2xl font-semibold text-gray-800">
                  {data.api_recommended_dose} mcg/ημ.
                </div>
                {data.strategy_used && (
                  <div className="text-xs text-gray-500">Στρατηγική: {data.strategy_used}</div>
                )}
              </div>
            </div>

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Συμφωνία δόσης</span>
                <span className="font-medium">{data.dose_concordance_percentage}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Απόλυτη διαφορά</span>
                <span className="font-medium">{data.absolute_dose_difference} mcg</span>
              </div>
            </div>

            {data.api_message && (
              <div className="border-t pt-4">
                <div className="text-xs uppercase text-gray-500 tracking-wide mb-1">Μήνυμα Αλγορίθμου</div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.api_message}</p>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={() => navigate(tables.formPath)} size="lg">
                Νέα Αξιολόγηση
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ResultPage;
